import { useFeatureValue } from '@growthbook/growthbook-react';
import { isGoogleOTP } from '@/utils/googleOTPData';

const PERSONALIZED_RECOMMENDATION_EXPERIMENT = 'personalized-recommendation-ux-flow';

const ELIGIBLE_PRODUCT_SLUGS = ['rugiet-ready', 'rugiet-men-ed'] as const;

/**
 * Checks if a product slug is eligible for personalized recommendation flow
 */
const isEligibleProductSlug = (slug: string | null | undefined): boolean => {
  return ELIGIBLE_PRODUCT_SLUGS.some((eligibleSlug) => slug === eligibleSlug);
};

/**
 * Checks if a pathname includes an eligible product
 */
const isEligiblePathname = (pathname: string): boolean => {
  return ELIGIBLE_PRODUCT_SLUGS.some((slug) => pathname.includes(slug));
};

interface UsePersonalizedRecommendationFlowOptions {
  /** Product slug to check */
  productSlug?: string | null;
  /** Whether the variant is RDT (dissolving tablet) */
  isRdt?: boolean;
  /** Pathname to check (alternative to productSlug) */
  pathname?: string;
}

/**
 * Centralized hook for personalized recommendation UX flow experiment.
 * Returns the feature value (0 if disabled, >0 if enabled).
 *
 * The experiment is enabled when:
 * - Product is 'rugiet-ready' or 'rugiet-men-ed'
 * - Variant is NOT RDT (if isRdt is provided)
 * - User is NOT in Google OTP flow
 *
 * @param options - Configuration options
 * @returns Feature value from GrowthBook
 */
export const usePersonalizedRecommendationFlow = (
  options: UsePersonalizedRecommendationFlowOptions = {},
): number => {
  const { productSlug, isRdt, pathname } = options;

  const isEligibleProduct = productSlug
    ? isEligibleProductSlug(productSlug)
    : pathname
      ? isEligiblePathname(pathname)
      : false;

  const isRdtVariant = isRdt === true;
  const isInOTPFlow = isGoogleOTP();

  const shouldEnableExperiment = isEligibleProduct && !isRdtVariant && !isInOTPFlow;

  const featureValue = useFeatureValue(
    shouldEnableExperiment ? PERSONALIZED_RECOMMENDATION_EXPERIMENT : '',
    0,
  );

  return shouldEnableExperiment ? featureValue : 0;
};
